import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { addDays, format } from 'date-fns';
import { upperCase } from 'lodash';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import { Jobs } from 'server/shared/collections/jobs';
import { Users } from 'server/shared/collections/users';
import {
  getJobsDefaultFilterSaveParams,
  normalizeJobsDefaultFilterForLoad,
} from 'server/shared/helpers/jobs-query/getEmptyJobsDefaultFilter';
import {
  getJobsMongoQuery,
  getMatchingShiftsForSearchText,
} from 'server/shared/helpers/jobs-query/getJobsMongoQuery';
import { Department } from 'server/shared/models/department';
import { Invitee } from 'server/shared/models/invitee';
import { Job } from 'server/shared/models/job';
import { PoolerJobsTabIdEnum } from 'server/shared/models/PoolerJobsTabId.enum';
import { RoleEnum } from 'server/shared/models/role.enum';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { PoolerUser } from 'server/shared/models/user';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { rxFilterIsNotEmpty } from 'src/helpers/filterIsNotEmpty';
import { getInitialJobIndexFiltersCollapsed } from 'src/helpers/getInitialJobIndexFiltersCollapsed';
import {
  Column,
  ColumnResizeManager,
  defaultPoolerJobColumns,
} from 'src/helpers/job/jobColumns';
import { queryParam$ } from 'src/helpers/queryParam$';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { PermissionsService } from '../../services/permissions.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';
import { SubscriptionService } from '../../shared/subscription.service';

@Component({
  selector: 'app-pooler-jobs-index',
  templateUrl: './pooler-jobs-index.component.html',
  styleUrls: [
    './pooler-jobs-index.component.scss',
    '../../../stylesheets/components/atoms/jobs-timeline.scss',
    '../../../stylesheets/components/atoms/jobs-list-view.scss',
  ],
})
export class PoolerJobsIndexComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  jobsQuery$ = new BehaviorSubject<
    | {
        query: Mongo.Query<Job>;
        dateSort: number;
        limit: number;
      }
    | undefined
  >(undefined);

  jobs$? = this.jobsQuery$.pipe(
    rxFilterIsNotEmpty(),
    distinctUntilChanged((previous, current) => {
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    switchMap(({ query, dateSort, limit }) => {
      return Jobs.find(query, {
        transform: this.transform.bind(this),
        sort: { date: dateSort, jobStart: 1, 'department.name': 1 },
        limit: limit,
      }).pipe(debounceTime(30));
    }),
    shareReplay(1),
  );

  daysList: {
    date: string;
    previousDateRangeStart?: string;
    previousDateRangeEnd?: string;
  }[] = [];

  today = format(new Date(), dbDayDateToken);

  jobsByDateMap$ = this.jobs$?.pipe(
    map((jobs) => {
      // Early return for empty jobs array
      if (!jobs.length) {
        this.daysList = [];
        return new Map<string, Job[]>();
      }

      const jobsByDateMap = new Map<string, Job[]>();
      const daysList: any[] = [];

      let previousJobDate: string | undefined;

      for (let i = 0; i < jobs.length; i++) {
        const job = jobs[i];
        const jobDate = job.date;

        // Get or create array for this date
        let jobsForDate = jobsByDateMap.get(jobDate);
        if (!jobsForDate) {
          jobsForDate = [];
          jobsByDateMap.set(jobDate, jobsForDate);

          // Only calculate date range info when we encounter a new date
          if (previousJobDate && previousJobDate !== jobDate) {
            const yesterday = format(
              addDays(new Date(jobDate), -1),
              dbDayDateToken,
            );

            daysList.push({
              date: jobDate,
              previousDateRangeStart:
                previousJobDate !== yesterday
                  ? format(
                      addDays(new Date(previousJobDate), 1),
                      dbDayDateToken,
                    )
                  : undefined,
              previousDateRangeEnd:
                previousJobDate !== yesterday ? yesterday : undefined,
            });
          } else if (!previousJobDate) {
            // First date encountered
            daysList.push({
              date: jobDate,
              previousDateRangeStart: undefined,
              previousDateRangeEnd: undefined,
            });
          }

          previousJobDate = jobDate;
        }

        jobsForDate.push(job);
      }

      this.daysList = daysList;
      return jobsByDateMap;
    }),
    shareReplay(1),
  );

  i18n = this.translationService.getTranslations('jobs');
  searchOptions: any = {};
  currentUserId?: string;
  currentShift?: string;
  shiftEnum = ShiftEnum;
  jobsSubscription?: Subscription;
  user?: PoolerUser;
  showCanceledJobs = false;
  onlyShowCanceledJobs = false;
  showOnlyActionRequiredBookableJobs = false;
  showOnlyActionRequiredNoOpenInviteesJobs = false;
  onyShowBookedJobs = false;
  activeTabId?: PoolerJobsTabIdEnum;
  dateRangeChips: string[] = [
    'ALL',
    'TODAY',
    'TOMORROW',
    'WEEK',
    'NEXT_WEEK',
    'MONTH',
    'CUSTOM',
  ];
  selectedRange: string = '';

  tabs: { label: string; value: PoolerJobsTabIdEnum; svgIcon: string }[] = [
    {
      label: 'new',
      value: PoolerJobsTabIdEnum.NEW,
      svgIcon: 'inbox',
    },
    {
      label: 'answered',
      value: PoolerJobsTabIdEnum.ANSWERED,
      svgIcon: 'e-mail-read-check',
    },
    {
      label: 'booked',
      value: PoolerJobsTabIdEnum.CLOSED,
      svgIcon: 'board-booked-star',
    },
    {
      label: 'archived',
      value: PoolerJobsTabIdEnum.ARCHIVED,
      svgIcon: 'archive-box',
    },
  ];

  activeView: 'tiles' | 'list' =
    (localStorage.getItem('jobActiveView') as 'tiles' | 'list') || 'tiles';
  filtersCollapsed = getInitialJobIndexFiltersCollapsed();

  @ViewChild('filterStickyStack', { read: ElementRef })
  filterStickyStackRef?: ElementRef<HTMLElement>;

  jobsIndexDayHeaderTopPx: number | null = null;
  jobsIndexDayHeaderTopFallbackPx = 114;
  /** Sticky offset below list column header row (see jobs-list-view). */
  jobsIndexListViewDayHeaderOffsetPx = 48;
  jobsIndexDayHeaderTopExtraPx = 85;
  filterStickyStackResizeObserver?: ResizeObserver;

  destroyed$ = new Subject<void>();

  columnResizeManager = new ColumnResizeManager(
    'poolerJobListColumnWidths',
    this.ngZone,
  );

  columns$ = this.columnResizeManager.columnWidths$.pipe(
    map((columnWidths) => {
      let columns: Column[] = [...defaultPoolerJobColumns];

      // Apply custom widths from localStorage
      const mappedColumns = columns.map((col) => ({
        ...col,
        width: columnWidths[col.key] || col.width,
      }));

      // Ensure CSS rules exist for all column keys with their widths
      this.columnResizeManager.ensureColumnKeysExist(mappedColumns);

      return mappedColumns;
    }),
    shareReplay(1),
  );

  tabParam$: Observable<PoolerJobsTabIdEnum> = this.route.queryParams.pipe(
    map((params) =>
      params.state
        ? (upperCase(params.state) as PoolerJobsTabIdEnum)
        : PoolerJobsTabIdEnum.NEW,
    ),
    // distinctUntilChanged is needed in case of other params changing
    distinctUntilChanged(),
  );

  $isLoadingJobs = this.tabParam$.pipe(
    switchMap((state) =>
      state === PoolerJobsTabIdEnum.ARCHIVED
        ? this.subscriptionService.archivedJobsLoading$
        : this.subscriptionService.jobs.loading$,
    ),
    rxNgZoneRun(this.ngZone),
  );
  userTracker?: Tracker.Computation;
  searchParam$: Observable<string | undefined> = this.route.queryParams.pipe(
    // weird query param name, stays like this so that links & saved filters still work
    map((params) => params.ALL),
    // distinctUntilChanged is needed in case of other params changing
    distinctUntilChanged(),
  );

  departmentParam$ = queryParam$(this.route, 'departments');
  selectedDepartmentIds$: Observable<string[]> = this.departmentParam$.pipe(
    map((departments) => {
      if (departments) {
        return departments.split(',').filter((id: string) => id);
      }
      return [];
    }),
    shareReplay(1),
  );

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService,
    private translationService: TranslationService,
    private route: ActivatedRoute,
    private router: Router,
    public permissionsService: PermissionsService,
    private subscriptionService: SubscriptionService,
  ) {}

  ngOnDestroy() {
    if (this.jobsSubscription) {
      this.jobsSubscription.unsubscribe();
    }
    if (this.userTracker) {
      this.userTracker.stop();
    }

    this.destroyed$.next();
    this.destroyed$.complete();
    this.filterStickyStackResizeObserver?.disconnect();
    this.columnResizeManager.cleanup();
  }

  ngOnInit() {
    this.subscriptionService.organisations.ready$
      .pipe(takeUntil(this.destroyed$))
      .subscribe();

    this.userTracker = Tracker.autorun(() => {
      let user = Users.findOne({
        _id: Meteor.userId(),
        role: RoleEnum.POOL,
      }) as PoolerUser;
      if (!user) {
        return;
      }
      this.ngZone.run(() => {
        this.user = user;
        this._loadDefaultFilters();
      });
    });
  }

  ngAfterViewInit() {
    this.initJobsIndexDayHeaderTopTracking();
  }

  private initJobsIndexDayHeaderTopTracking() {
    const stackEl = this.filterStickyStackRef?.nativeElement;
    if (typeof ResizeObserver === 'undefined') {
      this.updateJobsIndexDayHeaderTopFromFilterHeader();
      return;
    }
    this.filterStickyStackResizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.ngZone.run(() =>
          this.updateJobsIndexDayHeaderTopFromFilterHeader(),
        );
      });
    });
    if (stackEl) this.filterStickyStackResizeObserver.observe(stackEl);
    this.updateJobsIndexDayHeaderTopFromFilterHeader();
  }

  updateJobsIndexDayHeaderTopFromFilterHeader() {
    const stackH = this.filterStickyStackRef?.nativeElement?.offsetHeight ?? 0;
    const next = stackH + this.jobsIndexDayHeaderTopExtraPx;
    if (this.jobsIndexDayHeaderTopPx !== next) {
      this.jobsIndexDayHeaderTopPx = next;
      this.cdr.markForCheck();
    }
  }

  /** Grid collapse animates over ~0.2s; refresh after toggle and when it ends. */
  private scheduleStickyStackHeightRefresh(): void {
    const refresh = () =>
      this.ngZone.run(() => this.updateJobsIndexDayHeaderTopFromFilterHeader());
    requestAnimationFrame(refresh);
    setTimeout(refresh, 220);
  }

  onFilterCollapseTransitionEnd(event: TransitionEvent): void {
    if (event.target !== event.currentTarget) return;
    if (event.propertyName !== 'grid-template-rows') return;
    this.ngZone.run(() => this.updateJobsIndexDayHeaderTopFromFilterHeader());
  }

  transform(job: Job) {
    job.yourself = job.invitees.find(
      (invitee: Invitee) => invitee.userId == Meteor.userId(),
    );
    return job;
  }

  loadJobsByOptions(): void {
    if (!this.user) {
      console.log('missing user to load jobs');
    }
    let dateSort = 1;
    let limit = 200;
    if (this.activeTabId == PoolerJobsTabIdEnum.ARCHIVED) {
      dateSort = -1;
      this.subscriptionService.subscribeArchivedJobs(this.searchOptions);
    }
    this.jobsQuery$.next({
      query: this.searchOptions,
      dateSort,
      limit,
    });
  }

  saveDefaultFilter() {
    if (!this.user || !this.activeTabId) return;
    Meteor.call(
      'savePoolerDefaultFilter',
      this.activeTabId,
      getJobsDefaultFilterSaveParams(
        this.activeTabId,
        this.route.snapshot.queryParams as Record<
          string,
          string | null | undefined
        >,
      ),
    );
    this.toastService.add({
      type: 'SUCCESS',
      message: 'savedDefaultFilter',
      timeout: 5000,
    });
  }

  searchByText(text?: string | null) {
    this._setNewQueryParams({ ALL: text });
  }

  selectDepartments(departments?: Department[]) {
    const departmentIds = departments
      ? departments.map((department) => department.id)
      : [];
    this._setNewQueryParams({
      departments: departmentIds.length > 0 ? departmentIds.join(',') : null,
    });
  }

  enableState(state: PoolerJobsTabIdEnum, event?: Event): void {
    if (event?.currentTarget instanceof HTMLElement) {
      (event.currentTarget as HTMLElement).scrollIntoView({
        inline: 'start',
        block: 'nearest',
        behavior: 'smooth',
      });
    }
    this.activeTabId = state;
    // @ts-ignore
    this._loadDefaultFilters(state, true);
  }

  private _setNewQueryParams(newQueryParams: any, replace = false) {
    let extras: any = {
      relativeTo: this.route,
      queryParams: newQueryParams,
      replaceUrl: true,
      queryParamsHandling: 'merge',
    };
    if (replace) {
      delete extras.queryParamsHandling;
    }
    this.router.navigate([], extras).then(() => {
      if (!this.user) return;
      const searchText = this.route.snapshot.queryParams['ALL'];
      const matchingShifts = getMatchingShiftsForSearchText(
        searchText,
        this.i18n,
      );
      this.searchOptions = getJobsMongoQuery(
        this.route.snapshot.queryParams,
        this.user,
        {
          showCanceledWithArchived: this.showCanceledJobs,
          showOnlyCanceledJobs: this.onlyShowCanceledJobs,
          showOnlyActionRequiredBookableJobs:
            this.showOnlyActionRequiredBookableJobs,
          showOnlyActionRequiredNoOpenInviteesJobs:
            this.showOnlyActionRequiredNoOpenInviteesJobs,
          showOnlyBookedJobs: this.onyShowBookedJobs,
          matchingShifts,
        },
      );
      this.loadJobsByOptions();
    });
  }

  private _initFilterInputs(filterValues: any) {
    if (filterValues.createdBy) {
      this.currentUserId = filterValues.createdBy;
    } else {
      this.currentUserId = 'ALL';
    }
    if (filterValues.shift) {
      this.currentShift = filterValues.shift;
    } else {
      this.currentShift = 'ALL';
    }

    this.showCanceledJobs = filterValues['show-canceled'] === 'true';
    this.onlyShowCanceledJobs = filterValues['only-show-canceled'] === 'true';

    const legacyActionRequired =
      filterValues['show-action-required-only'] === 'true';

    this.showOnlyActionRequiredBookableJobs =
      filterValues['show-action-required-bookable-only'] === 'true' ||
      legacyActionRequired;
    this.showOnlyActionRequiredNoOpenInviteesJobs =
      filterValues['show-action-required-no-open-invitees-only'] === 'true' ||
      legacyActionRequired;

    this.onyShowBookedJobs = filterValues['only-show-booked'] === 'true';

    if (filterValues.range) {
      let splitRange = filterValues.range.split(',');
      this.selectedRange = splitRange[0];
    } else {
      this.selectedRange = 'ALL';
    }

    if (filterValues.state) {
      this.activeTabId = filterValues.state;
    }
  }

  private _loadDefaultFilters(
    tabId?: PoolerJobsTabIdEnum,
    resetQueryParams?: boolean,
  ) {
    let routeQueryParams = this.route.snapshot.queryParams;
    let defaultValues;
    let filterValues;
    if (tabId === undefined) {
      if (
        routeQueryParams.state &&
        routeQueryParams.state.toUpperCase() in PoolerJobsTabIdEnum
      ) {
        tabId = routeQueryParams.state.toUpperCase() as PoolerJobsTabIdEnum;
      } else {
        tabId = PoolerJobsTabIdEnum.NEW;
      }
    }

    if (this.user && this.user.defaultFilters && tabId !== undefined) {
      const filterForTab = this.user.defaultFilters[tabId];
      if (filterForTab && Object.keys(filterForTab)?.length) {
        defaultValues = this.user.defaultFilters[tabId];
      }
    }

    if (Object.keys(routeQueryParams).length !== 0 && !resetQueryParams) {
      //query param values
      filterValues = routeQueryParams;
    } else if (defaultValues) {
      //user default values
      filterValues = normalizeJobsDefaultFilterForLoad(defaultValues);
    } else {
      //origin default values
      filterValues = {
        range: 'ALL',
        state: tabId,
      };
    }

    if (filterValues.state && filterValues.state.toUpperCase() !== tabId) {
      let { state, ...rest } = filterValues;

      filterValues = {
        ...rest,
        state: tabId,
      };
    }
    this._setNewQueryParams(filterValues, true);
    this._initFilterInputs(filterValues);
  }

  filterByDateRange() {
    if (!this.user) return;
    const searchText = this.route.snapshot.queryParams['ALL'];
    const matchingShifts = getMatchingShiftsForSearchText(
      searchText,
      this.i18n,
    );
    this.searchOptions = getJobsMongoQuery(
      this.route.snapshot.queryParams,
      this.user,
      {
        showCanceledWithArchived: this.showCanceledJobs,
        showOnlyCanceledJobs: this.onlyShowCanceledJobs,
        showOnlyActionRequiredBookableJobs:
          this.showOnlyActionRequiredBookableJobs,
        showOnlyActionRequiredNoOpenInviteesJobs:
          this.showOnlyActionRequiredNoOpenInviteesJobs,
        showOnlyBookedJobs: this.onyShowBookedJobs,
        matchingShifts,
      },
    );
    this.loadJobsByOptions();
  }

  trackJob(index: number, job: Job) {
    return job._id;
  }

  toggleFilters(): void {
    this.filtersCollapsed = !this.filtersCollapsed;
    localStorage.setItem(
      'jobIndexFiltersCollapsed',
      this.filtersCollapsed.toString(),
    );
    this.scheduleStickyStackHeightRefresh();
  }

  toggleView() {
    this.activeView = this.activeView === 'tiles' ? 'list' : 'tiles';
    localStorage.setItem('jobActiveView', this.activeView);
  }

  onResizeStart = (event: MouseEvent, columnKey: string) =>
    this.columnResizeManager.onResizeStart(event, columnKey);

  trackByColumnKey = (index: number, column: Column) => column.key;
}
